import { request } from './client';
import type {
  AdminPoll,
  AdminSession,
  Candidate,
  ComplianceStatus,
  ContentReport,
  ElectoralBlackout,
  MediaItem,
  MediaRights,
  ReportStatus,
  Tag,
  TagWithCount,
  WhatsAppTransportKind,
  WhatsAppTransportState,
} from './types';

/**
 * Sign-in: a Google ID token from the "Entrar com Google" button (02-requirements.md
 * §Admin Authentication, Google-only since 2026-09-27). Google proves who; the
 * API's allowlist decides whether — a valid token for an address nobody listed
 * is a 403 `not_allowlisted`.
 */
export const signInWithGoogle = (idToken: string) =>
  request<AdminSession>('/admin/auth/google', { body: { idToken } });

/**
 * One function per admin route, and nothing else. Every rule these appear to
 * enforce is enforced by the API — the typed-title confirmation, the 409 on a
 * candidate that holds votes, the tick reset behind every destructive write
 * (02-requirements.md §Poll Administration). This file only names the calls.
 */

export const listAdminPolls = () =>
  request<{ polls: AdminPoll[] }>('/admin/polls').then((payload) => payload.polls);

/** The editor reads the public detail route: same poll, same candidates, in order. */
export const getPollDetail = (id: string) =>
  request<{ poll: AdminPoll; candidates: Candidate[] }>(`/admin/polls/${id}`);

/** Every category that exists, most-used first sorted by the API by name. */
export const listTags = () =>
  request<{ tags: TagWithCount[] }>('/tags').then((payload) => payload.tags);

/**
 * Create a category with no poll on it yet
 * (02-requirements.md §Category Administration, user decision 2026-08-17).
 *
 * Answers **409 `tag_exists`** when the name already belongs to a category —
 * matched on the normalized name, so "Bares" collides with "bares " — and the
 * body carries that category under `tag`. **400 `invalid_name`** for a name that
 * is blank once trimmed.
 */
export const createTag = (name: string, icon: string | null = null) =>
  // The created tag, without counts: it has no poll on it, and the list is
  // re-read by the mutation anyway.
  request<Tag>('/admin/tags', { body: { name, icon } });

/**
 * Rename a category, or give it an icon, or both — `PATCH /admin/tags/:id` takes
 * either field and an absent one means "leave it", never "clear it" (only an
 * explicit `icon: null` clears the icon).
 *
 * The rename reaches every poll carrying the category: the API rewrites
 * `polls.category` for each, because that column is the head of the list the app
 * prints. Answers **409 `tag_exists`** for a name another category holds; a
 * change of capitalization alone is not a collision.
 */
export const renameTag = (id: string, name: string) =>
  request<Tag>(`/admin/tags/${id}`, { method: 'PATCH', body: { name } });

/**
 * Give a category an icon, or clear it with `null`
 * (02-requirements.md §Category Icons).
 */
export const setTagIcon = (id: string, icon: string | null) =>
  request<Tag & { icon: string | null }>(`/admin/tags/${id}`, { method: 'PATCH', body: { icon } });

/**
 * Delete a category, taking it off every poll that carries it
 * (02-requirements.md §Category Deletion).
 *
 * Answers **409 `tag_last_on_poll`** when some poll carries this category and
 * nothing else — every poll keeps at least one — and the error body names those
 * polls so the screen can say which to fix.
 */
export const deleteTag = (id: string) =>
  request<void>(`/admin/tags/${id}`, { method: 'DELETE' });

export interface NewPoll {
  title: string;
  /** The poll's categories, in order; the first is its primary tag. */
  tags: string[];
  description?: string | null;
  coverImage?: string | null;
  /** Subject to the electoral rules. */
  political?: boolean;
  /** At least one, each with a picture — the API's schema refuses fewer. */
  candidates: { name: string; imageUrl: string }[];
}

/** `POST /polls` answers the created poll **with** its candidates and tags. */
export const createPoll = (body: NewPoll) =>
  request<{ poll: AdminPoll; candidates: Candidate[] }>('/polls', { body });

export interface PollPatch {
  title?: string;
  /** Replaces the whole category list; its head becomes `category`. */
  tags?: string[];
  description?: string | null;
  coverImage?: string | null;
  status?: 'active' | 'closed';
  /** Hides the poll from every app surface; false puts it back as it was. */
  paused?: boolean;
  political?: boolean;
}

export const updatePoll = (id: string, patch: PollPatch) =>
  request<AdminPoll>(`/polls/${id}`, { method: 'PATCH', body: patch });

/** `confirm` must be the poll's exact title; the API answers 400 confirm_mismatch. */
export const deletePoll = (id: string, confirm: string) =>
  request<void>(`/polls/${id}`, { method: 'DELETE', body: { confirm } });

export const addCandidate = (pollId: string, body: { name: string; imageUrl: string }) =>
  request<Candidate>(`/polls/${pollId}/candidates`, { body });

export const updateCandidate = (id: string, patch: { name?: string; imageUrl?: string }) =>
  request<Candidate>(`/candidates/${id}`, { method: 'PATCH', body: patch });

/** 409 `candidate_has_votes` unless `force` — the votes and ledger rows go with it. */
export const deleteCandidate = (id: string, force = false) =>
  request<void>(`/candidates/${id}`, { method: 'DELETE', body: { force } });

export const reorderCandidates = (pollId: string, candidateIds: string[]) =>
  request<{ candidates: Candidate[] }>(`/polls/${pollId}/candidates/order`, {
    method: 'PATCH',
    body: { candidateIds },
  }).then((payload) => payload.candidates);

export const listMedia = () =>
  request<{ images: MediaItem[] }>('/admin/media').then((payload) => payload.images);

/** The declaration every stored picture needs; the API answers 400 `rights_required` without it. */
export type RightsDeclaration = Pick<MediaRights, 'basis' | 'credit'>;

export function uploadMedia({ file, rights }: { file: File; rights: RightsDeclaration }) {
  const form = new FormData();
  // Before the file: the API reads the fields that precede it.
  form.append('rights', rights.basis);
  if (rights.credit?.trim()) form.append('credit', rights.credit.trim());
  form.append('file', file);
  // No content-type header: the browser writes the multipart boundary.
  return request<{ name: string; path: string }>('/admin/media', { form });
}

/** A URL is an import: the API fetches it and stores a local copy. */
export const importMedia = ({ url, rights }: { url: string; rights: RightsDeclaration }) =>
  request<{ name: string; path: string; source: string }>('/admin/media/import', {
    body: { url, rights: rights.basis, credit: rights.credit?.trim() || null },
  });

/** 409 `media_in_use` while a poll or candidate points at it; `media_bundled` never deletes. */
export const deleteMedia = (name: string) =>
  request<void>(`/admin/media/${encodeURIComponent(name)}`, { method: 'DELETE' });

/* Legal compliance (legal review 2026-09-28). */

export const getCompliance = () => request<ComplianceStatus>('/admin/compliance');

export const addBlackout = (body: { startsAt: string; endsAt: string; reason: string }) =>
  request<ElectoralBlackout>('/admin/electoral-blackouts', { body });

export const deleteBlackout = (id: string) =>
  request<void>(`/admin/electoral-blackouts/${id}`, { method: 'DELETE' });

export const listReports = (status?: ReportStatus) =>
  request<{ reports: ContentReport[] }>(`/admin/reports${status ? `?status=${status}` : ''}`).then(
    (payload) => payload.reports,
  );

export const resolveReport = (id: string, body: { status: ReportStatus; resolution?: string | null }) =>
  request<ContentReport>(`/admin/reports/${id}`, { method: 'PATCH', body });

/** The WhatsApp the worker speaks through: official Cloud API or Baileys. */
export const getWhatsAppTransport = () => request<WhatsAppTransportState>('/admin/whatsapp/transport');

/** 409 `transport_unavailable` when the worker has no credentials for that kind. */
export const setWhatsAppTransport = (kind: WhatsAppTransportKind) =>
  request<WhatsAppTransportState>('/admin/whatsapp/transport', { method: 'PUT', body: { kind } });

/** Baileys only: a code to type into WhatsApp → Aparelhos conectados → Conectar com número. */
export const requestPairingCode = (phone: string) =>
  request<{ code: string }>('/admin/whatsapp/pairing-code', { body: { phone } }).then((payload) => payload.code);
